/**
 * What is wrong right now, gathered from the checks that already exist and put
 * somewhere a person will actually see it.
 *
 * Every condition here was ALREADY detected before this module: a dropped
 * platform connection is what `derivePassiveStatus` computes, and a stale
 * warehouse is what `attentionItems` computes. Both were only ever rendered on
 * the one page that owns them — Settings, and /admin/bigquery-sync — so a
 * connection could lapse and a warehouse could fall eleven days behind while
 * every screen a person actually uses said nothing.
 *
 * The header said "Online" throughout, from a hardcoded pill that checked
 * nothing.
 *
 * This module only AGGREGATES. It computes no health of its own, so it cannot
 * disagree with the page that owns each check — the failure mode this codebase
 * keeps finding, where two surfaces derive the same fact separately and drift.
 *
 * @module services/systemHealth
 */

import type { PlatformAccount } from "@/types";
import { platformLabel } from "@/utils/platformLabel";

/** Who is entitled to see an issue. */
export type IssueAudience = "workspace" | "master";

export interface HealthIssue {
  /** Stable within a render, and unique — it is the React key. */
  id: string;
  /**
   * error = something is broken and data is being lost right now.
   * warning = degraded or at risk, but still working.
   */
  severity: "error" | "warning";
  audience: IssueAudience;
  /**
   * The platform this issue is about, as the id used in `channels[]` — or null
   * for an issue that is not about one platform.
   *
   * Set HERE rather than parsed back out of `title` downstream. The title is
   * Thai prose built for a human ("Google Analytics ไม่ได้เชื่อมต่อ"); anything
   * that re-derived the id from it would be a second place that decides what
   * platform an issue belongs to, and this module exists because two surfaces
   * deriving the same fact separately is how they drift.
   */
  platform: string | null;
  title: string;
  detail: string;
  /** Where the person goes to fix it. */
  href: string;
  /** Label for that link. */
  action: string;
}

/**
 * Statuses that mean the account is NOT delivering data.
 *
 * "unverified" is deliberately absent: it means nobody has tested the
 * connection, not that it failed, and raising it would train people to ignore
 * the indicator. `derivePassiveStatus` is the single source of these values.
 */
const BROKEN_STATUS: ReadonlySet<PlatformAccount["status"]> = new Set([
  "disconnected",
  "expired",
  "error",
]);

/**
 * Both spellings on purpose.
 *
 * `google_ads`/`google_analytics` are the PlatformType names used in code; the
 * rows in media_platform_accounts on the live workspace store `google` and
 * `analytics`, matching what media_campaigns uses. Only the short forms were
 * ever reaching this map, so `label()` fell through to the raw id and a client
 * was shown "analytics ไม่ได้เชื่อมต่อ" — and, since 0747239, so was the model.
 */
const label = platformLabel;

/**
 * Connections that have stopped delivering.
 *
 * Demo accounts are excluded: they have no credential by design, and reporting
 * them would put a permanent warning on every demo workspace.
 */
export const connectionIssues = (accounts: readonly PlatformAccount[]): HealthIssue[] =>
  accounts
    .filter((a) => a.accountType !== "demo_data" && BROKEN_STATUS.has(a.status))
    .map((a) => ({
      id: `connection:${a.id}`,
      // Every one of these means data is not arriving, so none is a warning.
      severity: "error" as const,
      audience: "workspace" as const,
      platform: a.platform,
      title: `${label(a.platform)} ไม่ได้เชื่อมต่อ`,
      detail:
        a.status === "expired"
          ? `โทเคนของ ${a.accountName} หมดอายุ — ข้อมูลใหม่จะไม่เข้ามาจนกว่าจะเชื่อมต่อใหม่`
          : a.status === "error"
            ? `${a.accountName} sync ไม่สำเร็จครั้งล่าสุด — ข้อมูลอาจไม่อัปเดต`
            : `${a.accountName} ยังไม่มีข้อมูลการเชื่อมต่อที่ใช้ได้`,
      href: "/settings?tab=integrations",
      action: "ไปที่การเชื่อมต่อ",
    }));

/** One queued/failed sync job, as the health check needs to see it. */
export interface SyncJobSummary {
  platform: string;
  status: string;
  errorMessage?: string | null;
  createdAt: string;
}

/**
 * A job queued this long with nobody claiming it means no worker is draining
 * the queue. Generous on purpose: a real sync is 48-120s of sequential API
 * calls, and several queued at once are processed one after another.
 */
const STUCK_QUEUE_MINUTES = 90;

/**
 * What the account row cannot tell you: that nothing is running.
 *
 * derivePassiveStatus reads stored fields, and every one of them looked healthy
 * while six jobs sat queued and unclaimed — because a job nobody picks up
 * writes no error, moves no timestamp, and changes no status. The account still
 * says "last synced yesterday, no error".
 *
 * This is the same lesson the BigQuery page already recorded in its own
 * attentionItems: comparing two internal timestamps cannot detect "everything
 * is switched off", because when nothing runs, neither of them moves. An
 * absolute test against the wall clock is the only thing that catches it.
 */
export const syncQueueIssues = (
  jobs: readonly SyncJobSummary[],
  nowMs: number = Date.now(),
): HealthIssue[] => {
  const issues: HealthIssue[] = [];

  const stuck = jobs.filter(
    (j) => j.status === "queued" && nowMs - new Date(j.createdAt).getTime() > STUCK_QUEUE_MINUTES * 60_000,
  );
  if (stuck.length > 0) {
    const platforms = [...new Set(stuck.map((j) => label(j.platform)))].join(", ");
    issues.push({
      id: "sync:stuck-queue",
      // Data is not arriving and nothing else reports it. This is the exact
      // silent failure the whole indicator exists for.
      severity: "error",
      audience: "workspace",
      // Deliberately null even though the jobs name platforms: a stuck queue
      // means no worker is draining ANY of it, so pinning it to one platform
      // would understate it. The affected names are in `detail`.
      platform: null,
      title: "งาน sync ค้างในคิว",
      detail: `มี ${stuck.length} งานรอคิวเกิน ${STUCK_QUEUE_MINUTES} นาที (${platforms}) — แปลว่าไม่มี worker มาดึงไปทำ ข้อมูลใหม่จะไม่เข้าจนกว่าจะแก้`,
      href: "/settings?tab=integrations",
      action: "ดูการเชื่อมต่อ",
    });
  }

  // The LATEST job per platform decides, not any failure in the window.
  //
  // This used to raise an issue for the first failed job it found in 48 hours,
  // whether or not the platform had synced happily since. A backfill of July
  // failed at 17:17 on one day; Facebook then synced successfully twice the
  // next morning, and the dashboard was still announcing "Facebook Ads sync
  // ไม่สำเร็จ" ten hours and two successes later, pointing at an account whose
  // data was complete and current. The question a reader is asking is "is this
  // platform broken NOW", and an old failure is not an answer to it.
  //
  // Sorted here rather than trusting the caller's order: getRecentSyncJobs does
  // return newest-first today, but this function takes the array as an argument
  // and a health check that silently depends on its caller's ORDER BY is one
  // refactor away from alarming on the oldest failure in the window.
  //
  // One entry per platform, not per job: a platform failing every attempt would
  // otherwise fill the panel with the same finding.
  const latestByPlatform = new Map<string, SyncJobSummary>();
  for (const j of jobs) {
    const seen = latestByPlatform.get(j.platform);
    if (!seen || j.createdAt > seen.createdAt) latestByPlatform.set(j.platform, j);
  }
  const failedByPlatform = new Map<string, SyncJobSummary>();
  for (const [platform, job] of latestByPlatform) {
    // `partial` is deliberately absent: it means rows arrived and something
    // else went wrong, so the connection is proven working and this panel —
    // which sends people to Settings to fix a connection — is the wrong place
    // to raise it.
    if (job.status === "failed") failedByPlatform.set(platform, job);
  }
  for (const [platform, job] of failedByPlatform) {
    issues.push({
      id: `sync:failed:${platform}`,
      severity: "error",
      audience: "workspace",
      // This one IS about a single platform, unlike the stuck queue above.
      platform,
      title: `${label(platform)} sync ไม่สำเร็จ`,
      detail: job.errorMessage?.trim()
        ? job.errorMessage.slice(0, 180)
        : "งาน sync ล้มเหลวโดยไม่มีข้อความอธิบาย",
      href: "/settings?tab=integrations",
      action: "ดูการเชื่อมต่อ",
    });
  }

  return issues;
};

/**
 * The warehouse problems /admin/bigquery-sync already computes.
 *
 * Takes the strings that page produces rather than recomputing them, so the two
 * cannot say different things about the same pipeline. Master-only, because the
 * warehouse is not a workspace-level concern and a client cannot act on it.
 */
export const bigQueryIssues = (attentionItems: readonly string[]): HealthIssue[] =>
  attentionItems.map((detail, i) => ({
    id: `bigquery:${i}`,
    // Degraded rather than broken: the app keeps working on Supabase when the
    // warehouse falls behind. It is still the master's problem to fix.
    severity: "warning" as const,
    audience: "master" as const,
    platform: null,
    title: "BigQuery ไม่ตรงกับข้อมูลต้นทาง",
    detail,
    href: "/admin/bigquery-sync",
    action: "ดูรายละเอียด",
  }));

/**
 * What this viewer should be shown, worst first.
 *
 * A non-master never sees master issues — not merely hidden in the UI, filtered
 * here, so a count cannot include something the list then omits.
 */
export const visibleIssues = (issues: readonly HealthIssue[], isMaster: boolean): HealthIssue[] =>
  issues
    .filter((i) => i.audience === "workspace" || isMaster)
    // Errors above warnings; original order preserved within each, because that
    // order is the owning page's own.
    .slice()
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1));
